import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../types/navigation';
import { BottomNavBar } from '../../../components/BottomNavBar';
import { loyaltyPointService, LoyaltyPoint } from '../../../services/loyaltyPointService';

import { createLogger } from '../../../utils/logger';
const logger = createLogger('LoyaltyPointData');

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
  loyaltyPoint?: any;
  loyaltyPointId?: string;
};

type LoyaltyPointData = {
  id: string;
  category: string;
  loyaltyPointType: string;
  companyName: string;
  accountNumber: string;
  username: string;
  password: string;
  websiteUrl: string;
  notes: string;
};

type InfoItemProps = {
  label: string;
  value: string;
  isPassword?: boolean;
  onCopy?: () => void;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value, isPassword = false, onCopy }) => {
  const [showPassword, setShowPassword] = useState(false);

  if (isPassword) {
    return (
      <View style={styles.infoItem}>
        <Text style={styles.label}>{label}</Text>
        <View style={styles.passwordContainer}>
          <Image
            source={require('../../../assets/icons/lock.png')}
            style={styles.lockIcon}
          />
          <Text style={styles.value}>
            {showPassword ? value : '•'.repeat(value.length)}
          </Text>
          <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
            <Image
              source={showPassword ? 
                require('../../../assets/icons/Eye.png') :
                require('../../../assets/icons/Eye-closed.png')
              }
              style={styles.eyeIcon}
            />
          </TouchableOpacity>
          {onCopy && (
            <TouchableOpacity onPress={onCopy} style={styles.copyButton}>
              <Image
                source={require('../../../assets/icons/copy.png')}
                style={styles.copyIcon}
              />
            </TouchableOpacity>
          )}
        </View>
        <View style={styles.separator} />
      </View>
    );
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.valueContainer}>
        <Text style={styles.value}>{value}</Text>
        {onCopy && (
          <TouchableOpacity onPress={onCopy} style={styles.copyButton}>
            <Image
              source={require('../../../assets/icons/copy.png')}
              style={styles.copyIcon}
            />
          </TouchableOpacity>
        )}
      </View>
      <View style={styles.separator} />
    </View>
  );
};

const LoyaltyPointCard: React.FC<{ loyaltyPoint: LoyaltyPointData; onDelete: (id: string) => void; viewOnly?: boolean }> = ({
  loyaltyPoint,
  onDelete,
  viewOnly = false
}) => {
  const navigation = useNavigation<RootStackNavigationProp>();

  const handleCopy = (text: string, label: string) => {
    // In a real app, you would use a clipboard library
    Alert.alert('Copied', `${label} copied to clipboard`);
  };

  const handleEdit = () => {
    navigation.navigate('Loyalty', {
      screen: 'LoyaltyPointInputScreen',
      params: { loyaltyPoint }
    } as any);
  };

  const handleDelete = () => {
    Alert.alert(
      'Delete Loyalty Program',
      'Are you sure you want to delete this loyalty program? This action cannot be undone.',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => onDelete(loyaltyPoint.id),
        },
      ]
    );
  };

  // Convert category ID to display name
  const getCategoryDisplayName = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      'retail-shopping': 'Retail & Shopping',
      'travel-hospitality': 'Travel & Hospitality',
      'food-beverage': 'Food & Beverage',
      'credit-cards-financial': 'Credit Cards & Financial',
      'health-wellness': 'Health & Wellness',
      'other': 'Other'
    };
    return categoryMap[category] || category;
  };

  return (
    <View style={styles.loyaltyPointCard}>
      <View style={styles.cardHeader}>
        <Text style={styles.cardTitle}>{loyaltyPoint.companyName}</Text>
        {!viewOnly && (
          <View style={styles.cardActions}>
            <TouchableOpacity style={styles.editButton} onPress={handleEdit}>
              <Image
                source={require('../../../assets/icons/edit.png')}
                style={styles.actionIcon}
              />
              <Text style={styles.actionText}>Edit</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.deleteButton} onPress={handleDelete}>
              <Image
                source={require('../../../assets/icons/delete.png')}
                style={styles.actionIcon}
              />
              <Text style={styles.actionText}>Delete</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      <InfoItem 
        label="Category" 
        value={getCategoryDisplayName(loyaltyPoint.category)} 
      />
      <InfoItem 
        label="Program Type" 
        value={loyaltyPoint.loyaltyPointType} 
      />
      <InfoItem 
        label="Account Number" 
        value={loyaltyPoint.accountNumber} 
        isPassword={true}
        onCopy={() => handleCopy(loyaltyPoint.accountNumber, 'Account Number')}
      />
      {loyaltyPoint.username && (
        <InfoItem 
          label="Username" 
          value={loyaltyPoint.username} 
          isPassword={true}
          onCopy={() => handleCopy(loyaltyPoint.username, 'Username')}
        />
      )}
      {loyaltyPoint.password && (
        <InfoItem 
          label="Password" 
          value={loyaltyPoint.password} 
          isPassword={true}
          onCopy={() => handleCopy(loyaltyPoint.password, 'Password')}
        />
      )}
      {loyaltyPoint.websiteUrl && (
        <InfoItem 
          label="Website/URL" 
          value={loyaltyPoint.websiteUrl} 
          onCopy={() => handleCopy(loyaltyPoint.websiteUrl, 'Website URL')}
        />
      )}
      {loyaltyPoint.notes && (
        <InfoItem 
          label="Notes" 
          value={loyaltyPoint.notes} 
        />
      )}
    </View>
  );
};

export const LoyaltyPointDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<{ params: RouteParams }, 'params'>>();
  const [loyaltyPoint, setLoyaltyPoint] = useState<LoyaltyPointData | null>(null);
  const [loading, setLoading] = useState(true);

  const params = route.params || {};
  const { targetUserId, viewOnly = false } = params;

  useEffect(() => {
    loadLoyaltyPointData();
  }, []);

  const loadLoyaltyPointData = async () => {
    try {
      const params = route.params as any;

      if (params?.loyaltyPoint) {
        // Data passed directly from dashboard
        setLoyaltyPoint(params.loyaltyPoint);
      } else if (params?.loyaltyPointId) {
        // Load data from vault using ID
        const data = await loyaltyPointService.getLoyaltyPointCredentials(params.loyaltyPointId, targetUserId);
        setLoyaltyPoint({
          id: params.loyaltyPointId,
          ...data
        });
      }
    } catch (error) {
      logger.error('Error loading loyalty point data:', error);
      Alert.alert('Error', 'Failed to load loyalty program data.');
      navigation.goBack();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (loyaltyPointId: string) => {
    try {
      await loyaltyPointService.deleteLoyaltyPointCredentials(loyaltyPointId);
      Alert.alert(
        'Success',
        'Loyalty program deleted successfully.',
        [
          {
            text: 'OK',
            onPress: () => navigation.navigate('Loyalty', { screen: 'LoyaltyPointDashboardScreen' } as any)
          }
        ]
      );
    } catch (error) {
      logger.error('Error deleting loyalty point:', error);
      Alert.alert('Error', 'Failed to delete loyalty program. Please try again.');
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!loyaltyPoint) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Loyalty program not found.</Text>
          <TouchableOpacity 
            style={styles.backButton} 
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.backButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
      </View>

      <Text style={styles.title}>Loyalty Program Details</Text>

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {viewOnly && (
            <View style={styles.viewOnlyBanner}>
              <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
            </View>
          )}
          <LoyaltyPointCard loyaltyPoint={loyaltyPoint} onDelete={handleDelete} viewOnly={viewOnly} />
          
          <TouchableOpacity 
            style={styles.backToDashboardButton}
            onPress={() => navigation.navigate('Loyalty', { screen: 'LoyaltyPointDashboardScreen' } as any)}
          >
            <Text style={styles.backToDashboardText}>Back to Dashboard</Text>
          </TouchableOpacity>
          
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loyaltyPointCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
  },
  cardTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    flex: 1,
  },
  cardActions: {
    flexDirection: 'row',
    gap: ms(15),
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#29BFC0',
    paddingHorizontal: ms(12),
    paddingVertical: ms(8),
    borderRadius: ms(20),
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FF5A5A',
    paddingHorizontal: ms(12),
    paddingVertical: ms(8),
    borderRadius: ms(20),
  },
  actionIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#FFFFFF',
    marginRight: ms(5),
  },
  actionText: {
    color: '#FFFFFF',
    fontSize: ms(12, 0.3),
    fontWeight: '500',
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
    flex: 1,
  },
  valueContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(8),
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(8),
  },
  lockIcon: {
    width: s(20),
    height: s(20),
    marginRight: ms(10),
    tintColor: '#1C2C4F',
  },
  eyeIcon: {
    width: s(24),
    height: s(24),
    marginLeft: ms(10),
    tintColor: '#6B7280',
  },
  copyButton: {
    marginLeft: ms(10),
    padding: ms(5),
  },
  copyIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  backToDashboardButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(15),
    paddingHorizontal: ms(30),
    borderRadius: ms(25),
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(20),
  },
  backToDashboardText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(100),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#FFFFFF',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(20),
  },
  errorText: {
    fontSize: ms(18, 0.3),
    color: '#FFFFFF',
    marginBottom: ms(20),
    textAlign: 'center',
  },
  backButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});